import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';

// Composant boutons de partage vintage
const VintageShareButtons = ({ url, title }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=Lisez cet article : ${encodedUrl}`
  };
  
  return (
    <div className="flex gap-2 mt-3 pt-3 border-t border-dashed border-gray-400">
      <span className="text-xs uppercase tracking-wider font-bold mr-2">Partager :</span>
      <a 
        href={shareLinks.twitter} 
        target="_blank" 
        rel="noopener noreferrer"
        className="px-2 py-1 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-700 transition-colors"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        Télex
      </a>
      <a 
        href={shareLinks.facebook} 
        target="_blank" 
        rel="noopener noreferrer"
        className="px-2 py-1 border-2 border-black text-black text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        Tribune
      </a>
      <a 
        href={shareLinks.linkedin} 
        target="_blank" 
        rel="noopener noreferrer"
        className="px-2 py-1 border-2 border-black text-black text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        Gazette
      </a>
      <a 
        href={shareLinks.email}
        className="px-2 py-1 bg-gray-800 text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-600 transition-colors"
        style={{ fontFamily: "'Courier New', monospace" }}
      >
        Courrier
      </a>
    </div>
  );
};

const JournalPage = () => {
  const { schoolSlug, classSlug, year } = useParams();
  const [journal, setJournal] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJournal();
  }, [schoolSlug, classSlug, year]);

  const loadJournal = async () => {
    try {
      const response = await api.get(`/articles/journal/${schoolSlug}/${classSlug}/${year}`);
      setJournal(response.data.journal);
      setArticles(response.data.articles);
    } catch (error) {
      console.error('Error loading journal:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getFullUrl = (path) => {
    return `${window.location.origin}${path}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 font-serif text-xl">Chargement du journal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5dc]">
      {/* Newspaper Header */}
      <header className="border-b-4 border-double border-black py-4 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          {/* Masthead */}
          <div className="border-b border-black pb-2 mb-2">
            <p className="text-xs tracking-[0.5em] uppercase">La voix de l'école</p>
          </div>
          
          <h1 className="font-serif text-5xl md:text-7xl font-black tracking-tight uppercase" 
              style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}>
            THE DAILY GLOBE
          </h1>
          
          <div className="flex justify-center items-center gap-4 mt-2 border-t border-b border-black py-2">
            <span className="text-sm font-serif">{journal?.school || schoolSlug}</span>
            <span className="text-2xl">•</span>
            <span className="text-sm font-serif uppercase font-bold">{journal?.class || classSlug}</span>
            <span className="text-2xl">•</span>
            <span className="text-sm font-serif">{year}</span>
          </div>
          
          <p className="text-xs mt-2 tracking-widest uppercase">
            « Former les journalistes de demain »
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {articles.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-gray-400">
            <p className="font-serif text-2xl text-gray-600">Aucun article publié pour le moment</p>
            <p className="font-serif text-gray-500 mt-2">Les articles validés par les professeurs apparaîtront ici</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-0 border-l border-black">
            {/* Featured Article (First one, large) */}
            {articles[0] && (
              <motion.article 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="md:col-span-2 border-r border-b border-black p-6 bg-white"
              >
                <Link to={articles[0].publicUrl} className="block group">
                  {articles[0].photoUrl && (
                    <div className="mb-4 border border-black">
                      <img 
                        src={articles[0].photoUrl} 
                        alt={articles[0].headline}
                        className="w-full h-64 object-cover grayscale"
                      />
                      {articles[0].photoCaption && (
                        <p className="text-xs italic p-2 border-t border-black bg-gray-50">
                          {articles[0].photoCaption}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <h2 className="font-serif text-3xl md:text-4xl font-black uppercase leading-tight group-hover:underline"
                      style={{ fontFamily: "'Playfair Display', serif" }}>
                    {articles[0].headline}
                  </h2>
                  
                  {articles[0].subheadline && (
                    <p className="font-serif text-lg mt-2 text-gray-700 italic">
                      {articles[0].subheadline}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 mt-3 text-sm border-t border-b border-gray-300 py-2">
                    <span className="font-bold uppercase tracking-wide">Par {articles[0].studentName}</span>
                    {articles[0].studentClass && (
                      <>
                        <span className="text-gray-400">|</span>
                        <span className="text-xs bg-black text-white px-2 py-0.5 uppercase tracking-wider">
                          Promo {articles[0].studentClass}
                        </span>
                      </>
                    )}
                    <span>•</span>
                    <span className="italic">{formatDate(articles[0].publishedAt)}</span>
                  </div>
                  
                  <p className="font-serif text-lg mt-4 leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:float-left first-letter:mr-2">
                    {articles[0].lead || (articles[0].correctedContent || articles[0].content)?.substring(0, 300)}...
                  </p>
                  
                  <p className="text-right mt-4 font-bold text-sm uppercase tracking-wide">
                    ► Lire la suite
                  </p>
                </Link>
                
                <VintageShareButtons 
                  url={getFullUrl(articles[0].publicUrl)} 
                  title={articles[0].headline} 
                />
              </motion.article>
            )}
            
            {/* Secondary Articles */}
            <div className="border-r border-black bg-white">
              {articles.slice(1, 4).map((article, index) => (
                <motion.article 
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 border-b border-black last:border-b-0"
                >
                  <Link to={article.publicUrl} className="block group">
                    {article.photoUrl && (
                      <div className="mb-2 border border-black">
                        <img 
                          src={article.photoUrl} 
                          alt={article.headline}
                          className="w-full h-32 object-cover grayscale"
                        />
                      </div>
                    )}
                    
                    <h3 className="font-serif text-lg font-bold uppercase leading-tight group-hover:underline"
                        style={{ fontFamily: "'Playfair Display', serif" }}>
                      {article.headline}
                    </h3>
                    
                    <div className="text-xs mt-1 text-gray-600">
                      Par <span className="font-bold">{article.studentName}</span>
                      {article.studentClass && (
                        <span className="ml-1 bg-gray-200 px-1 text-[10px] uppercase">
                          {article.studentClass}
                        </span>
                      )}
                    </div>
                    
                    <p className="font-serif text-sm mt-2 text-gray-700 line-clamp-3">
                      {article.lead || (article.correctedContent || article.content)?.substring(0, 150)}...
                    </p>
                  </Link>
                  
                  <VintageShareButtons 
                    url={getFullUrl(article.publicUrl)} 
                    title={article.headline} 
                  />
                </motion.article>
              ))}
            </div>
            
            {/* More Articles Grid */}
            {articles.length > 4 && (
              <div className="md:col-span-3 grid md:grid-cols-4 border-t-4 border-double border-black">
                {articles.slice(4).map((article, index) => (
                  <motion.article 
                    key={article.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 + index * 0.05 }}
                    className="p-4 border-r border-b border-black bg-white last:border-r-0"
                  >
                    <Link to={article.publicUrl} className="block group">
                      {article.photoUrl && (
                        <img 
                          src={article.photoUrl} 
                          alt={article.headline}
                          className="w-full h-24 object-cover grayscale mb-2 border border-black"
                        />
                      )}
                      
                      <h4 className="font-serif text-sm font-bold uppercase leading-tight group-hover:underline">
                        {article.headline}
                      </h4>
                      
                      <p className="text-xs mt-1 text-gray-500">
                        {article.studentName}
                        {article.studentClass && (
                          <span className="ml-1 text-[9px] bg-gray-200 px-1 uppercase">
                            {article.studentClass}
                          </span>
                        )}
                      </p>
                    </Link>
                    
                    <div className="flex gap-1 mt-2 flex-wrap">
                      <a 
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(getFullUrl(article.publicUrl))}&text=${encodeURIComponent(article.headline)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-1 py-0.5 bg-black text-white text-[9px] font-bold uppercase hover:bg-gray-700"
                      >
                        Télex
                      </a>
                      <a 
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getFullUrl(article.publicUrl))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-1 py-0.5 border border-black text-black text-[9px] font-bold uppercase hover:bg-black hover:text-white"
                      >
                        Tribune
                      </a>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t-4 border-double border-black bg-white py-6 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="font-serif text-sm">
            THE DAILY GLOBE • {journal?.school} • Promotion {year}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Une publication des étudiants en journalisme • Propulsé par DailyGlobe School
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <span className="text-xs uppercase tracking-wider border-b-2 border-black pb-1">Édition Papier</span>
            <span className="text-xs uppercase tracking-wider text-gray-400">Télétype</span>
            <span className="text-xs uppercase tracking-wider text-gray-400">Archives</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JournalPage;
